import { createReducer } from "@reduxjs/toolkit";
import type { TeacherPayoutEligibilityVM } from "@shared-kernel/application/ports/teacher/teacher-repository";
import {
  resetTeacherRetrievePayoutRequestEligibility,
  teacherRetrievePayoutRequestEligibility,
} from "./retrieve-payout-request-eligibility";
import { teacherCancelLesson } from "@academy-context/write/application/use-cases/teacher/cancel-lesson/cancel-lesson";
import { createLesson } from "@academy-context/write/application/use-cases/teacher/lesson-creation/create-lesson";
import { getCreatedLessonPaidToTeacher } from "@academy-context/write/domain/services/locations";
import type { RetrievalState } from "@redux/state/shared-types";

const initialState: RetrievalState<TeacherPayoutEligibilityVM> = {
  fetching: "idle",
  data: {
    isEligible: false,
    summary: {
      totalHours: 0,
      totalAmount: 0,
      lastPayoutDate: null,
    },
  },
};

export const retrievePayoutRequestEligibilityReducer = createReducer(initialState, builder => {
  builder
    .addCase(teacherRetrievePayoutRequestEligibility.pending, state => {
      state.fetching = "pending";
    })
    .addCase(teacherRetrievePayoutRequestEligibility.fulfilled, (state, action) => {
      state.fetching = "success";
      state.data = action.payload;
    })
    .addCase(teacherRetrievePayoutRequestEligibility.rejected, state => {
      state.fetching = "failed";
    })
    // Optimistic updates: the backend refreshes the payout summary asynchronously, so refetching right
    // after a lesson change would return stale figures. `isEligible` only matters to on-demand payouts,
    // where it boils down to having hours left to pay.
    .addCase(createLesson.fulfilled, (state, { meta: { arg: lesson } }) => {
      state.data.summary.totalHours += lesson.hours.quantity;
      state.data.summary.totalAmount += getCreatedLessonPaidToTeacher(lesson);
      state.data.isEligible = state.data.summary.totalHours > 0;
    })
    .addCase(teacherCancelLesson.fulfilled, (state, { meta: { arg: lesson } }) => {
      state.data.summary.totalHours -= lesson.hours;
      state.data.summary.totalAmount -= lesson.paidToTeacher;
      state.data.isEligible = state.data.summary.totalHours > 0;
    })
    .addCase(resetTeacherRetrievePayoutRequestEligibility, () => {
      return initialState;
    });
});
